import React, { FC, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { OAuthApp, OAuthScope, UpdateOAuthAppInput } from 'models/oauth-app';
import AddClinicAutocomplete from './add-clinic-autocomplete.component';
import { ClinicOption } from './types';

type EditOAuthAppDialogProps = {
    app: OAuthApp | null;
    clinics: ClinicOption[];
    clinicsLoading: boolean;
    onCancel: () => void;
    onSubmit: (id: string, input: UpdateOAuthAppInput) => void;
};

const isValidRedirectUri = (uri: string): boolean => {
    try {
        const parsed = new URL(uri);
        if (parsed.hash) return false;
        if (parsed.protocol === 'https:') return true;
        return parsed.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(parsed.hostname);
    } catch {
        return false;
    }
};

const EditOAuthAppDialog: FC<EditOAuthAppDialogProps> = ({
    app,
    clinics,
    clinicsLoading,
    onCancel,
    onSubmit,
}: EditOAuthAppDialogProps) => {
    const { t: translate } = useTranslation(['app-settings', 'common']);
    const [name, setName] = useState('');
    const [vendorName, setVendorName] = useState('');
    const [redirectUrisText, setRedirectUrisText] = useState('');
    const [scopes, setScopes] = useState<OAuthScope[]>([]);
    const [selectedClinics, setSelectedClinics] = useState<ClinicOption[]>([]);
    const [allowClientCredentials, setAllowClientCredentials] = useState(false);

    // Prefill from the app being edited. Allowed organizations that are no
    // longer in the parent-clinic list (renamed/deactivated) keep their id
    // as the display name so they stay visible and removable.
    useEffect(() => {
        if (!app) return;
        setName(app.name);
        setVendorName(app.vendorName);
        setRedirectUrisText(app.redirectUris.join('\n'));
        setScopes(app.allowedScopes);
        setAllowClientCredentials(app.allowClientCredentials);
        const clinicNamesById = new Map(clinics.map((clinic) => [clinic._id, clinic.name]));
        setSelectedClinics(
            app.allowedOrganizations.map((organizationId) => ({
                _id: organizationId,
                name: clinicNamesById.get(organizationId) ?? organizationId,
            })),
        );
    }, [app, clinics]);

    const redirectUris = redirectUrisText
        .split('\n')
        .map((uri) => uri.trim())
        .filter(Boolean);
    const invalidUris = redirectUris.filter((uri) => !isValidRedirectUri(uri));

    const toggleScope = (scope: OAuthScope, checked: boolean) => {
        setScopes((current) => (checked ? [...current, scope] : current.filter((s) => s !== scope)));
    };

    const handleSubmit = () => {
        if (!app) return;
        onSubmit(app._id, {
            name: name.trim(),
            vendorName: vendorName.trim(),
            redirectUris,
            allowedScopes: scopes,
            allowedOrganizations: selectedClinics.map((clinic) => clinic._id),
            allowClientCredentials,
        });
    };

    const canSubmit =
        name.trim().length > 0 &&
        vendorName.trim().length > 0 &&
        redirectUris.length > 0 &&
        invalidUris.length === 0 &&
        scopes.length > 0 &&
        selectedClinics.length > 0;

    return (
        <DHDialog
            open={app !== null}
            onClose={onCancel}
            maxWidth="sm"
            fullWidth
            title={translate('app-settings:EDIT_OAUTH_APP')}
            actions={
                <Box display="flex" gap={1}>
                    <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
                        {translate('common:SAVE')}
                    </Button>
                </Box>
            }
        >
            <Box mb={2}>
                <TextField
                    fullWidth
                    required
                    label={translate('app-settings:OAUTH_APP_NAME')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
            </Box>
            <Box mb={2}>
                <TextField
                    fullWidth
                    required
                    label={translate('app-settings:OAUTH_APP_VENDOR')}
                    value={vendorName}
                    onChange={(e) => setVendorName(e.target.value)}
                />
            </Box>
            <Box mb={2}>
                <TextField
                    fullWidth
                    required
                    multiline
                    minRows={2}
                    label={translate('app-settings:OAUTH_APP_REDIRECT_URIS')}
                    helperText={
                        invalidUris.length > 0
                            ? translate('app-settings:OAUTH_APP_REDIRECT_URIS_INVALID', { uri: invalidUris[0] })
                            : translate('app-settings:OAUTH_APP_REDIRECT_URIS_HELP')
                    }
                    error={invalidUris.length > 0}
                    value={redirectUrisText}
                    onChange={(e) => setRedirectUrisText(e.target.value)}
                />
            </Box>
            <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                    {translate('app-settings:OAUTH_APP_ORGANIZATIONS')}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                    {translate('app-settings:OAUTH_APP_ORGANIZATIONS_HELP')}
                </Typography>
                <AddClinicAutocomplete
                    clinics={clinics}
                    clinicsLoading={clinicsLoading}
                    selectedClinics={selectedClinics}
                    onClinicSelected={(clinic) => setSelectedClinics((current) => [...current, clinic])}
                />
                {selectedClinics.length > 0 && (
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{translate('app-settings:OAUTH_APP_CLINIC_NAME')}</TableCell>
                                    <TableCell>{translate('app-settings:OAUTH_APP_ORGANIZATION_ID')}</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedClinics.map((clinic) => (
                                    <TableRow key={clinic._id}>
                                        <TableCell>
                                            <Typography variant="body2">{clinic.name}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontFamily="monospace">
                                                {clinic._id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title={translate('common:DELETE')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        setSelectedClinics((current) =>
                                                            current.filter((selected) => selected._id !== clinic._id),
                                                        )
                                                    }
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
            <Box mb={1}>
                <Typography variant="subtitle2" gutterBottom>
                    {translate('app-settings:OAUTH_APP_SCOPES')}
                </Typography>
                <FormGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={scopes.includes(OAuthScope.orders_read)}
                                onChange={(_, checked) => toggleScope(OAuthScope.orders_read, checked)}
                            />
                        }
                        label={translate('app-settings:API_CLIENT_SCOPE_ORDERS_READ')}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={scopes.includes(OAuthScope.orders_write)}
                                onChange={(_, checked) => toggleScope(OAuthScope.orders_write, checked)}
                            />
                        }
                        label={translate('app-settings:API_CLIENT_SCOPE_ORDERS_WRITE')}
                    />
                </FormGroup>
            </Box>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={allowClientCredentials}
                        disabled={!app?.confidential}
                        onChange={(_, checked) => setAllowClientCredentials(checked)}
                    />
                }
                label={
                    <Box>
                        <Typography variant="body2">{translate('app-settings:OAUTH_APP_TRUSTED')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {translate('app-settings:OAUTH_APP_TRUSTED_HELP')}
                        </Typography>
                    </Box>
                }
            />
        </DHDialog>
    );
};

export default EditOAuthAppDialog;
